import React, { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import {
  Card,
  CardBody,
  Col,
  Row,
  Table,
  Button,
  Badge,
  Spinner,
} from "reactstrap"
import DatePicker from "react-datepicker"
import Select from "react-select"
import Breadcrumbs from "components/Common/Breadcrumb"
import { getUsersList } from "store/userlist/action"
import { post } from "helpers/api_helper"
import { GET_AI_USAGE_LOG } from "helpers/url_helper"
import { customStylesSelect } from "common/data/settings"
import moment from "moment"
import "react-datepicker/dist/react-datepicker.css"

const PAGE_SIZE = 50

const ALL_USERS_OPTION = { label: "All Users", value: 0 }

const LOG_TYPE_OPTIONS = [
  { label: "Blocks", value: "blocks" },
  { label: "Date", value: "date" },
  { label: "Project", value: "project" },
  { label: "Session", value: "session" },
  { label: "Model", value: "model" },
]

const DEFAULT_LOG_TYPE = LOG_TYPE_OPTIONS.find(o => o.value === "date")

const COLUMNS = [
  { key: "index", label: "#", width: 50, sortable: false },
  { key: "datetime", label: "Date / Time", width: 150, sortable: true },
  { key: "user", label: "User", width: 140, sortable: true },
  { key: "logType", label: "Log Type", width: 110, sortable: true },
  { key: "logName", label: "Log Name", width: 180, sortable: true },
  { key: "inputTokens", label: "Input Tokens", width: 110, sortable: true },
  { key: "outputTokens", label: "Output Tokens", width: 110, sortable: true },
  { key: "totalTokens", label: "Total Tokens", width: 110, sortable: true },
  { key: "model", label: "Model", width: 120, sortable: true },
]

const AIUsageReport = () => {
  document.title = "AI Usage Report | FlexiTracBoard"

  const dispatch = useDispatch()
  const { userList } = useSelector(state => ({
    userList: state?.userlist?.alluserslist?.data || [],
  }))

  const today = new Date()
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

  const [fromDate, setFromDate] = useState(firstOfMonth)
  const [toDate, setToDate] = useState(today)
  const [selectedUsers, setSelectedUsers] = useState([ALL_USERS_OPTION])
  const [logType, setLogType] = useState(DEFAULT_LOG_TYPE)
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" })
  const [colWidths, setColWidths] = useState(() =>
    COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: c.width }), {})
  )
  const resizingRef = useRef(null)

  const userOptions = [
    ALL_USERS_OPTION,
    ...userList
      .slice()
      .sort((a, b) => (a.username || "").localeCompare(b.username || ""))
      .map(u => ({ label: u.username, value: u.id })),
  ]

  const isAllSelected = selectedUsers.some(u => u.value === 0)

  useEffect(() => {
    dispatch(getUsersList())
  }, [])

  const fetchData = useCallback(
    async pageNum => {
      setLoading(true)
      try {
        const payload = {
          userid: isAllSelected || selectedUsers.length !== 1
            ? 0
            : selectedUsers[0].value,
          fromdate: moment(fromDate).format("YYYY-MM-DD"),
          todate: moment(toDate).add(1, "days").format("YYYY-MM-DD"),
          logtype: logType?.value || "",
          projectfolderpath: "",
          page: pageNum,
          pagesize: PAGE_SIZE,
        }

        const response = await post(GET_AI_USAGE_LOG, payload)
        let rows =
          response?.data ||
          response?.datas ||
          response?.items ||
          response?.result ||
          (Array.isArray(response) ? response : [])

        if (Array.isArray(rows)) {
          if (!isAllSelected && selectedUsers.length > 1) {
            const ids = new Set(selectedUsers.map(u => u.value))
            rows = rows.filter(r => ids.has(r.userId ?? r.userid))
          }
          setTableData(rows)
          setTotalCount(
            response?.totalCount ??
              response?.total ??
              response?.totalcount ??
              rows.length
          )
        } else {
          setTableData([])
          setTotalCount(0)
        }
      } catch {
        setTableData([])
        setTotalCount(0)
      } finally {
        setLoading(false)
      }
    },
    [fromDate, toDate, selectedUsers, logType, isAllSelected]
  )

  const handleSearch = () => {
    setHasSearched(true)
    setPage(1)
    fetchData(1)
  }

  useEffect(() => {
    if (hasSearched) fetchData(page)
  }, [page])

  const handleUserChange = selected => {
    if (!selected || selected.length === 0) {
      setSelectedUsers([ALL_USERS_OPTION])
      return
    }
    const latestAll = selected[selected.length - 1]?.value === 0
    const prevHadAll = selectedUsers.some(s => s.value === 0)
    if (latestAll) {
      setSelectedUsers([ALL_USERS_OPTION])
    } else if (prevHadAll) {
      setSelectedUsers(selected.filter(s => s.value !== 0))
    } else {
      setSelectedUsers(selected)
    }
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  const fmt = val => {
    if (val == null || val === "") return "-"
    const m = moment(val)
    return m.isValid() ? m.format("DD MMM YYYY HH:mm") : val
  }

  const cellVal = (...keys) => row => {
    for (const k of keys) {
      if (row[k] != null && row[k] !== "") return row[k]
    }
    return "-"
  }

  const getSortValue = (row, key) => {
    switch (key) {
      case "datetime":
        return (
          row.createdAt ??
          row.createdat ??
          row.logDate ??
          row.logdate ??
          row.date ??
          row.timestamp
        )
      case "user":
        return cellVal(
          "userName",
          "username",
          "userEmail",
          "useremail",
          "userid",
          "userId"
        )(row)
      case "logType":
        return row.logType || row.logtype
      case "logName":
        return cellVal("logName", "logname")(row)
      case "inputTokens":
        return row.inputTokens ?? row.inputtokens
      case "outputTokens":
        return row.outputTokens ?? row.outputtokens
      case "totalTokens": {
        const inputTk = row.inputTokens ?? row.inputtokens
        const outputTk = row.outputTokens ?? row.outputtokens
        return (
          row.totalTokens ??
          row.totaltokens ??
          (inputTk != null && outputTk != null ? inputTk + outputTk : null)
        )
      }
      case "model":
        return cellVal("model", "modelName", "modelname", "aiModel")(row)
      default:
        return null
    }
  }

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return tableData
    const dir = sortConfig.direction === "asc" ? 1 : -1
    return [...tableData].sort((a, b) => {
      const va = getSortValue(a, sortConfig.key)
      const vb = getSortValue(b, sortConfig.key)
      if (va == null && vb == null) return 0
      if (va == null) return -1 * dir
      if (vb == null) return 1 * dir

      if (sortConfig.key === "datetime") {
        const ma = moment(va)
        const mb = moment(vb)
        if (ma.isValid() && mb.isValid()) return (ma.valueOf() - mb.valueOf()) * dir
      }

      if (
        ["inputTokens", "outputTokens", "totalTokens"].includes(
          sortConfig.key
        )
      ) {
        return (Number(va) - Number(vb)) * dir
      }

      return String(va).localeCompare(String(vb)) * dir
    })
  }, [tableData, sortConfig])

  const handleSort = key => {
    setSortConfig(prev => {
      if (prev.key !== key) return { key, direction: "asc" }
      if (prev.direction === "asc") return { key, direction: "desc" }
      return { key: null, direction: "asc" }
    })
  }

  const handleResizeStart = (e, key) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startWidth = colWidths[key]

    const onMouseMove = moveEvent => {
      const newWidth = Math.max(50, startWidth + (moveEvent.clientX - startX))
      setColWidths(prev => ({ ...prev, [key]: newWidth }))
    }
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
      resizingRef.current = null
    }
    resizingRef.current = key
    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("mouseup", onMouseUp)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Admin" breadcrumbItem="AI Usage Report" />
          <div className="d-flex justify-content-end mb-3">
            <Link to="/aiusagedashboard" className="btn btn-outline-primary btn-sm">
              <i className="bx bx-line-chart me-1" /> View Dashboard
            </Link>
          </div>
          <Card>
            <CardBody>
              <Row className="mb-3 align-items-end g-2">
                <Col md={2} sm={6}>
                  <label className="form-label mb-1">From Date</label>
                  <DatePicker
                    dateFormat="yyyy-MM-dd"
                    selected={fromDate}
                    onChange={setFromDate}
                    className="form-control date-icon-back"
                    placeholderText="From date"
                    maxDate={toDate}
                  />
                </Col>
                <Col md={2} sm={6}>
                  <label className="form-label mb-1">To Date</label>
                  <DatePicker
                    dateFormat="yyyy-MM-dd"
                    selected={toDate}
                    onChange={setToDate}
                    className="form-control date-icon-back"
                    placeholderText="To date"
                    minDate={fromDate}
                    maxDate={new Date()}
                  />
                </Col>
                <Col md={4} sm={12}>
                  <label className="form-label mb-1">User(s)</label>
                  <Select
                    isMulti
                    value={selectedUsers}
                    onChange={handleUserChange}
                    options={userOptions}
                    classNamePrefix="select2-selection"
                    styles={customStylesSelect}
                    placeholder="Select user(s)..."
                    closeMenuOnSelect={false}
                  />
                </Col>
                <Col md={2} sm={6}>
                  <label className="form-label mb-1">Log Type</label>
                  <Select
                    value={logType}
                    onChange={setLogType}
                    options={LOG_TYPE_OPTIONS}
                    classNamePrefix="select2-selection"
                    styles={customStylesSelect}
                    placeholder="Select log type..."
                  />
                </Col>
                <Col md={2} sm={6}>
                  <Button
                    color="primary"
                    className="w-100"
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <i className="bx bx-search me-1" />
                        Search
                      </>
                    )}
                  </Button>
                </Col>
              </Row>

              {hasSearched && !loading && (
                <Row className="mb-2">
                  <Col>
                    <span className="text-muted font-size-13">
                      {tableData.length === 0
                        ? "No records found."
                        : `Showing ${(page - 1) * PAGE_SIZE + 1}–${
                            (page - 1) * PAGE_SIZE + tableData.length
                          }${totalCount > tableData.length ? ` of ${totalCount}` : ""} record${tableData.length !== 1 ? "s" : ""}`}
                    </span>
                  </Col>
                </Row>
              )}

              <div className="table-responsive">
                <Table
                  className="table table-bordered table-hover align-middle mb-0 font-size-13"
                  style={{ tableLayout: "fixed" }}
                >
                  <colgroup>
                    {COLUMNS.map(col => (
                      <col key={col.key} style={{ width: colWidths[col.key] }} />
                    ))}
                  </colgroup>
                  <thead className="table-light">
                    <tr>
                      {COLUMNS.map(col => (
                        <th
                          key={col.key}
                          className={col.sortable ? "position-relative user-select-none" : "position-relative"}
                          style={{
                            cursor: col.sortable ? "pointer" : "default",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          onClick={col.sortable ? () => handleSort(col.key) : undefined}
                        >
                          {col.label}
                          {col.sortable && sortConfig.key === col.key && (
                            <i
                              className={`bx ${
                                sortConfig.direction === "asc"
                                  ? "bx-chevron-up"
                                  : "bx-chevron-down"
                              } ms-1`}
                            />
                          )}
                          <span
                            onMouseDown={e => handleResizeStart(e, col.key)}
                            style={{
                              position: "absolute",
                              right: 0,
                              top: 0,
                              height: "100%",
                              width: 6,
                              cursor: "col-resize",
                              userSelect: "none",
                            }}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="text-center py-4">
                          <Spinner color="primary" />
                        </td>
                      </tr>
                    ) : sortedData.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center text-muted py-5">
                          {hasSearched
                            ? "No AI usage records found for the selected filters."
                            : "Use the filters above and click Search to view AI usage data."}
                        </td>
                      </tr>
                    ) : (
                      sortedData.map((row, idx) => {
                        const inputTk = row.inputTokens ?? row.inputtokens
                        const outputTk = row.outputTokens ?? row.outputtokens
                        const totalTk =
                          row.totalTokens ??
                          row.totaltokens ??
                          (inputTk != null && outputTk != null
                            ? inputTk + outputTk
                            : null)
                        return (
                          <tr key={idx}>
                            <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                            <td>
                              {fmt(
                                row.createdAt ??
                                  row.createdat ??
                                  row.logDate ??
                                  row.logdate ??
                                  row.date ??
                                  row.timestamp
                              )}
                            </td>
                            <td>
                              {cellVal(
                                "userName",
                                "username",
                                "userEmail",
                                "useremail",
                                "userid",
                                "userId"
                              )(row)}
                            </td>
                            <td>
                              {(row.logType || row.logtype) ? (
                                <Badge color="info" pill className="font-size-11">
                                  {row.logType || row.logtype}
                                </Badge>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td
                              style={{
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={row.logName || row.logname || ""}
                            >
                              {row.logName || row.logname || "-"}
                            </td>
                            <td className="text-end">
                              {inputTk != null
                                ? inputTk.toLocaleString()
                                : "-"}
                            </td>
                            <td className="text-end">
                              {outputTk != null
                                ? outputTk.toLocaleString()
                                : "-"}
                            </td>
                            <td className="text-end">
                              {totalTk != null
                                ? totalTk.toLocaleString()
                                : "-"}
                            </td>
                            <td>
                              {cellVal("modelsused",
                                "model",
                                "modelName",
                                "modelname",
                                "aiModel"
                              )(row)}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                  <Button
                    size="sm"
                    color="secondary"
                    disabled={page === 1 || loading}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <i className="bx bx-chevron-left" /> Previous
                  </Button>
                  <span className="font-size-13 text-muted">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    size="sm"
                    color="secondary"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next <i className="bx bx-chevron-right" />
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </React.Fragment>
  )
}

export default AIUsageReport
